import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Monitor, Play, Square, CheckCircle, XCircle, AlertTriangle,
  Zap, Clock, ChevronDown, ChevronUp, Eye, Settings
} from 'lucide-react';
import { useUserId } from '../context/UserAuthContext';

const API = window.location.origin;

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    idle:             { color: 'text-zinc-400 bg-zinc-800',     label: 'Idle' },
    starting:         { color: 'text-blue-400 bg-blue-900/40',  label: 'Starting…' },
    running:          { color: 'text-emerald-400 bg-emerald-900/40', label: 'Running' },
    thinking:         { color: 'text-yellow-400 bg-yellow-900/40',  label: 'Thinking…' },
    waiting_approval: { color: 'text-orange-400 bg-orange-900/40',  label: 'Awaiting Approval' },
    done:             { color: 'text-emerald-400 bg-emerald-900/40', label: 'Done' },
    stopped:          { color: 'text-zinc-400 bg-zinc-800',     label: 'Stopped' },
    error:            { color: 'text-red-400 bg-red-900/40',    label: 'Error' },
  };
  const cfg = map[status] || map.idle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'running' || status === 'thinking' ? 'animate-pulse bg-current' : 'bg-current'}`} />
      {cfg.label}
    </span>
  );
}

// ── Consent modal ─────────────────────────────────────────────────────────────
function ConsentModal({ onAccept }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#141414] border border-[#2a2a2a] rounded-2xl max-w-md w-full p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center">
            <AlertTriangle size={20} className="text-orange-400" />
          </div>
          <h2 className="text-lg font-semibold text-white">Before You Start</h2>
        </div>
        <div className="space-y-3 text-[13px] text-zinc-400 leading-relaxed mb-6">
          <p>
            <strong className="text-zinc-200">Computer Use</strong> lets Claude AI control a
            virtual Linux desktop inside a sandboxed environment to complete tasks on your behalf.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Claude operates a <strong className="text-zinc-300">virtual desktop</strong> — not your real Mac or files.</li>
            <li>Claude may encounter <strong className="text-zinc-300">untrusted web content</strong> while browsing.</li>
            <li>Claude will <strong className="text-zinc-300">pause and ask</strong> before any form submission, login, or purchase.</li>
            <li>Never share real passwords or payment info through this feature.</li>
          </ul>
        </div>
        <button
          onClick={onAccept}
          className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm transition-colors"
          data-testid="cu-consent-accept"
        >
          I Understand, Let's Go
        </button>
      </div>
    </div>
  );
}

// ── Approval modal ─────────────────────────────────────────────────────────────
function ApprovalModal({ action, inp, message, onDecide }) {
  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-40 p-4">
      <div className="bg-[#141414] border border-orange-500/30 rounded-2xl max-w-sm w-full p-6 shadow-2xl">
        <div className="flex items-center gap-2.5 mb-3">
          <AlertTriangle size={18} className="text-orange-400 shrink-0" />
          <h3 className="text-sm font-semibold text-white">Approval Required</h3>
        </div>
        <p className="text-xs text-zinc-300 mb-1">{message}</p>
        <div className="mt-2 mb-4 bg-[#0f0f0f] rounded-lg px-3 py-2 text-xs font-mono text-zinc-400 max-h-28 overflow-y-auto">
          <p className="text-orange-300 font-semibold">{action}</p>
          {inp && Object.keys(inp).length > 0 && (
            <pre className="mt-1 text-zinc-500 whitespace-pre-wrap break-all">
              {JSON.stringify(inp, null, 2)}
            </pre>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onDecide('approve')}
            className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors flex items-center justify-center gap-1.5"
            data-testid="cu-approve-btn"
          >
            <CheckCircle size={14} />
            Approve
          </button>
          <button
            onClick={() => onDecide('deny')}
            className="flex-1 py-2 rounded-lg bg-red-900/40 hover:bg-red-800/40 text-red-400 text-sm font-medium transition-colors flex items-center justify-center gap-1.5 border border-red-700/30"
            data-testid="cu-deny-btn"
          >
            <XCircle size={14} />
            Deny
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Log entry ─────────────────────────────────────────────────────────────────
function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const typeMap = {
    action:  { color: 'text-blue-400',    icon: '▶' },
    message: { color: 'text-emerald-400', icon: '●' },
    denied:  { color: 'text-red-400',     icon: '✕' },
  };
  const cfg = typeMap[entry.type] || { color: 'text-zinc-400', icon: '·' };
  const ts = entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';

  if (entry.type === 'message') {
    return (
      <div className="px-3 py-2 border-b border-[#1a1a1a]">
        <span className="text-[10px] text-zinc-600 mr-1.5">{ts}</span>
        <span className="text-[11px] text-emerald-300 leading-relaxed">{entry.text}</span>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-[#1a1a1a]">
      <button className="w-full text-left flex items-center gap-2" onClick={() => setOpen(!open)}>
        <span className={`text-[10px] ${cfg.color} shrink-0`}>{cfg.icon}</span>
        <span className={`flex-1 text-[11px] font-mono ${cfg.color} truncate`}>
          {entry.action || entry.type}
        </span>
        <span className="text-[10px] text-zinc-600">{ts}</span>
        {entry.input && Object.keys(entry.input).length > 0 && (
          <span className="text-zinc-600">{open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
        )}
      </button>
      {open && entry.input && Object.keys(entry.input).length > 0 && (
        <div className="mt-1.5 ml-4 bg-[#0f0f0f] rounded p-2 text-[10px] font-mono text-zinc-500 max-h-20 overflow-y-auto">
          {JSON.stringify(entry.input, null, 2)}
        </div>
      )}
      {entry.result && entry.result !== 'OK' && (
        <p className="mt-1 ml-4 text-[10px] text-red-400">{entry.result}</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ComputerUsePage() {
  const userId = useUserId();

  // Consent
  const [consented, setConsented] = useState(() => {
    try { return !!localStorage.getItem('cu_consent_v1'); } catch { return false; }
  });

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [actionLog, setActionLog] = useState([]);
  const [approval, setApproval] = useState(null); // {action, input, message}
  const [claudeMsg, setClaudeMsg] = useState('');

  // Input
  const [task, setTask] = useState('');
  const [approveEvery, setApproveEvery] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const wsRef = useRef(null);
  const logEndRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Track session id in a ref for WebSocket callback
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [actionLog]);

  // ── WebSocket ────────────────────────────────────────────────────────────
  const connectWs = useCallback((sid) => {
    if (wsRef.current) { wsRef.current.close(); }
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/api/computer-use/ws/${sid}`;
    const ws = new WebSocket(url);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'screenshot') {
          setScreenshot(msg.data);
        } else if (msg.type === 'status') {
          setStatus(msg.status);
          setStatusMsg(msg.message || '');
          if (msg.status === 'waiting_approval') {
            /* approval modal set separately */
          }
        } else if (msg.type === 'action_log') {
          setActionLog(prev => [...prev, msg]);
        } else if (msg.type === 'message') {
          setClaudeMsg(msg.text);
          setActionLog(prev => [...prev, { ...msg, type: 'message' }]);
        } else if (msg.type === 'approval_required') {
          setApproval({ action: msg.action, input: msg.input, message: msg.message });
        } else if (msg.type === 'error') {
          setStatus('error');
          setStatusMsg(msg.message);
        }
      } catch (_e) { /* ignore */ }
    };

    ws.onerror = () => setStatus('error');
    wsRef.current = ws;
    return ws;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => { wsRef.current?.close(); };
  }, []);

  // ── Start session ─────────────────────────────────────────────────────────
  const startSession = async () => {
    if (!task.trim()) return;
    setStatus('starting');
    setStatusMsg('Launching virtual desktop…');
    setScreenshot(null);
    setActionLog([]);
    setClaudeMsg('');
    setApproval(null);

    try {
      const res = await fetch(`${API}/api/computer-use/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task, user_id: userId, approve_every: approveEvery }),
      });
      if (!res.ok) {
        const err = await res.json();
        setStatus('error');
        setStatusMsg(err.detail || 'Failed to start');
        return;
      }
      const data = await res.json();
      setSessionId(data.session_id);
      connectWs(data.session_id);
    } catch (err) {
      setStatus('error');
      setStatusMsg(String(err));
    }
  };

  // ── Stop session ──────────────────────────────────────────────────────────
  const stopSession = async () => {
    if (wsRef.current) {
      try { wsRef.current.send(JSON.stringify({ type: 'stop' })); } catch (_e) { /* ignore */ }
      wsRef.current.close();
      wsRef.current = null;
    }
    if (sessionId) {
      try {
        await fetch(`${API}/api/computer-use/stop/${sessionId}`, { method: 'POST' });
      } catch (_e) { /* ignore */ }
    }
    setStatus('stopped');
    setSessionId(null);
  };

  // ── Approval ──────────────────────────────────────────────────────────────
  const handleApproval = async (decision) => {
    setApproval(null);
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: decision }));
    } else if (sessionId) {
      await fetch(`${API}/api/computer-use/approve/${sessionId}?decision=${decision}`, { method: 'POST' });
    }
  };

  const isActive = ['starting', 'running', 'thinking', 'waiting_approval'].includes(status);
  const isDone = ['done', 'stopped', 'error'].includes(status);
  const acceptConsent = () => {
    try { localStorage.setItem('cu_consent_v1', '1'); } catch (_e) { /* ignore */ }
    setConsented(true);
  };

  if (!consented) return <ConsentModal onAccept={acceptConsent} />;

  return (
    <div className="flex flex-col bg-[#0f0f0f] text-white" style={{ height: 'calc(100vh - 0px)' }} data-testid="computer-use-page">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#1e1e1e] bg-[#0a0a0a] shrink-0">
        <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
          <Monitor size={16} className="text-blue-400" />
        </div>
        <div>
          <h1 className="text-sm font-semibold text-zinc-100">Computer Use</h1>
          <p className="text-[10px] text-zinc-500">Claude controls a virtual desktop</p>
        </div>
        <div className="flex-1" />
        <StatusBadge status={status} />
      </div>

      {/* ── Task bar ───────────────────────────────────────────────────────── */}
      <div className="px-5 py-3 border-b border-[#1a1a1a] bg-[#0c0c0c] shrink-0">
        <div className="flex items-center gap-2 max-w-5xl mx-auto">
          <div className="relative flex-1">
            <input
              value={task}
              onChange={e => setTask(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !isActive) startSession(); }}
              placeholder="Describe what you want Claude to do on the virtual desktop…"
              disabled={isActive}
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-blue-500/50 transition-colors disabled:opacity-50"
              data-testid="cu-task-input"
            />
          </div>

          {/* Settings toggle */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`shrink-0 p-2.5 rounded-xl border transition-colors ${showSettings ? 'bg-white/10 border-white/20 text-white' : 'bg-white/5 border-transparent text-zinc-400 hover:text-white'}`}
            data-testid="cu-settings-btn"
          >
            <Settings size={15} />
          </button>

          {isActive ? (
            <button
              onClick={stopSession}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 transition-colors text-sm font-medium"
              data-testid="cu-stop-btn"
            >
              <Square size={14} fill="currentColor" />
              Stop
            </button>
          ) : (
            <button
              onClick={startSession}
              disabled={!task.trim()}
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
              data-testid="cu-start-btn"
            >
              <Play size={14} fill="currentColor" />
              Start
            </button>
          )}
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div className="mt-2.5 max-w-5xl mx-auto flex items-center gap-4 px-1">
            <label className="flex items-center gap-2 cursor-pointer group" data-testid="cu-approve-toggle">
              <div
                onClick={() => setApproveEvery(!approveEvery)}
                className={`w-9 h-5 rounded-full relative transition-colors ${approveEvery ? 'bg-orange-500' : 'bg-zinc-700'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${approveEvery ? 'left-4' : 'left-0.5'}`} />
              </div>
              <span className="text-xs text-zinc-400 group-hover:text-zinc-200 transition-colors">
                Approve every action
              </span>
            </label>
            <span className="text-[10px] text-zinc-600">
              When off, Claude only pauses for consequential actions
            </span>
          </div>
        )}
      </div>

      {/* ── Main area ──────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Live screen */}
        <div className="relative flex-1 flex items-center justify-center bg-[#060606] overflow-hidden">
          {approval && (
            <ApprovalModal
              action={approval.action}
              inp={approval.input}
              message={approval.message}
              onDecide={handleApproval}
            />
          )}

          {screenshot ? (
            <img
              src={`data:image/png;base64,${screenshot}`}
              alt="Virtual desktop"
              className="max-w-full max-h-full object-contain rounded shadow-lg"
              style={{ imageRendering: 'auto' }}
              data-testid="cu-screen"
            />
          ) : (
            <div className="text-center">
              {status === 'idle' || isDone ? (
                <>
                  <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                    <Monitor size={28} className="text-blue-400" />
                  </div>
                  {status === 'done' && (
                    <p className="text-emerald-400 text-sm font-medium mb-1">Task complete!</p>
                  )}
                  {status === 'error' && (
                    <div className="text-center max-w-xs">
                      <p className="text-red-400 text-sm font-medium mb-1">Error</p>
                      {statusMsg && <p className="text-zinc-500 text-xs leading-relaxed mb-2">{statusMsg}</p>}
                      {statusMsg && statusMsg.includes('credit') && (
                        <a
                          href="https://console.anthropic.com/settings/billing"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Add Anthropic Credits →
                        </a>
                      )}
                    </div>
                  )}
                  {status === 'idle' && (
                    <p className="text-zinc-600 text-sm mt-2">
                      Type a task above and click Start
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex gap-1.5 justify-center mb-3">
                    {[0, 150, 300].map(delay => (
                      <div key={delay} className="w-2.5 h-2.5 rounded-full bg-blue-400 animate-bounce"
                        style={{ animationDelay: `${delay}ms` }} />
                    ))}
                  </div>
                  <p className="text-zinc-400 text-sm">{statusMsg || 'Starting virtual desktop…'}</p>
                </>
              )}
            </div>
          )}

          {/* Claude message overlay */}
          {claudeMsg && isActive && (
            <div className="absolute bottom-3 left-3 right-3 max-w-2xl mx-auto">
              <div className="bg-black/75 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3">
                <div className="flex items-start gap-2">
                  <Zap size={13} className="text-blue-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-zinc-200 leading-relaxed">{claudeMsg}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action log */}
        <div className="w-72 shrink-0 border-l border-[#1a1a1a] bg-[#0a0a0a] flex flex-col" data-testid="cu-action-log">
          <div className="px-3 py-2.5 border-b border-[#1a1a1a] flex items-center gap-2">
            <Eye size={12} className="text-zinc-500" />
            <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">Action Log</span>
            <span className="ml-auto text-[10px] text-zinc-600">{actionLog.length} events</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {actionLog.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-xs text-zinc-600">
                Actions will appear here
              </div>
            ) : (
              actionLog.map((entry, i) => <LogEntry key={i} entry={entry} />)
            )}
            <div ref={logEndRef} />
          </div>

          {/* Status / turn counter */}
          {sessionId && (
            <div className="px-3 py-2 border-t border-[#1a1a1a]">
              <span className={`text-[10px] leading-relaxed ${status === 'error' ? 'text-red-400' : 'text-zinc-600'}`}>
                {statusMsg || (status === 'idle' ? 'Ready' : status)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
